import { CircularProgress, Grid, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  LegacyAlertDialog,
  LegacyDialog,
} from '~/legacy-components/LegacyDialog';
import {
  LegacyAutocomplete,
  LegacyMuiButton,
  LegacyMuiTextField,
} from '~/legacy-components/LegacyForms';
import { LegacyRichTextarea } from '~/legacy-components/LegacyRichTextarea';

import { Props, useSendEmailComponent } from './useSendEmailComponent';
import { useSxStyles } from './useStyles';

const EMAIL_FORM_ID = 'send-email-form';
const EMAIL_RECIPIENTS_PLACEHOLDER =
  'ex: john.doe@gmail.com,jane.doe@gmail.com';

export const LegacyEmailDialog = ({ open, onClose }: Props) => {
  const {
    allEmployees,
    control,
    errorMessage,
    errorDialogVisible,
    orgUnits,
    loading,
    recipientsCount,
    recipientsConfigVisible,
    onDialogClose,
    onErrorDialogClose,
    onSubmit,
    toggleRecipientsVisibility,
    updateRecipientsCount,
  } = useSendEmailComponent({ open, onClose });
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const test = Object.keys(allEmployees).map((empId) => parseInt(empId, 10));

  return (
    <>
      <LegacyDialog
        open={open}
        maxWidth="md"
        onClose={onDialogClose}
        formId={EMAIL_FORM_ID}
        showSave={false}
        showCancel={true}
        showCustomButton={true}
        customIcon={'fa fa-paper-plane'}
        customText={`${t('laSend')}`}
        customCancelText={`${t('laClose')}`}
        title={'laSendEmail'}
        icon={'fa fa-file-text-o'}
      >
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <Grid container justifyContent="flex-end">
              <LegacyMuiButton onClick={toggleRecipientsVisibility}>
                {`${t('laRecipients')} (${recipientsCount})`}
              </LegacyMuiButton>
            </Grid>
            <form id={EMAIL_FORM_ID} onSubmit={onSubmit}>
              <Grid container direction="column">
                <Grid item>
                  <Typography variant="subtitle2">
                    {`${t('laSubject')}:`}
                  </Typography>
                </Grid>
                <Grid item>
                  <Controller
                    control={control}
                    name="subject"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <LegacyMuiTextField
                        type="text"
                        value={value}
                        sx={sxStyles.input}
                        onChange={onChange}
                      />
                    )}
                  />
                </Grid>
                {recipientsConfigVisible && (
                  <>
                    <Grid item>
                      <Typography variant="subtitle2" sx={sxStyles.marginTop}>
                        {`${t('laRecipients')}:`}
                      </Typography>
                    </Grid>
                    <Grid container item alignItems="center">
                      <Grid item xs={4} sm={2} md={2}>
                        <Typography variant="body2">
                          {`${t('laUnits')}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={8} sm={10} md={10}>
                        <Controller
                          control={control}
                          name="units"
                          render={({ field: { onChange, value } }) => (
                            <LegacyAutocomplete
                              placeholder={`${t('laAddUnits')}`}
                              options={orgUnits}
                              getOptionLabel={(c) => c.fOrgUnitName}
                              onChange={(e, value) => {
                                onChange(value.map((v) => v.fOrgUnitId));
                                updateRecipientsCount();
                              }}
                              value={
                                orgUnits.filter((o) =>
                                  value?.includes(o.fOrgUnitId),
                                ) || []
                              }
                              multiple
                              renderInput={(params) => (
                                <LegacyMuiTextField {...params} />
                              )}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Grid container item alignItems="center">
                      <Grid item xs={4} sm={2} md={2}>
                        <Typography variant="body2">
                          {`${t('laEmployees')}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={8} sm={10} md={10}>
                        <Controller
                          control={control}
                          name="employees"
                          render={({ field: { onChange, value } }) => (
                            <LegacyAutocomplete
                              placeholder={`${t('laAddEmployees')}`}
                              options={test}
                              getOptionLabel={(c) =>
                                `${allEmployees[c].fEmpFirstName} ${allEmployees[c].fEmpLastName}`
                              }
                              onChange={(e, value) => {
                                onChange(value);
                                updateRecipientsCount();
                              }}
                              value={value as number[]}
                              multiple
                              renderInput={(params) => (
                                <LegacyMuiTextField {...params} />
                              )}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Typography variant="subtitle2">
                        {`${t('laEmail')}:`}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Controller
                        control={control}
                        name="emails"
                        defaultValue=""
                        render={({ field: { onChange } }) => (
                          <LegacyMuiTextField
                            type="text"
                            sx={sxStyles.input}
                            placeholder={EMAIL_RECIPIENTS_PLACEHOLDER}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              onChange(e.target.value);
                              updateRecipientsCount();
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                <Grid item>
                  <Typography sx={sxStyles.marginTop} variant="subtitle2">
                    {`${t('laBody')}:`}
                  </Typography>
                </Grid>
                <Grid item>
                  <Controller
                    control={control}
                    name="body"
                    defaultValue=""
                    render={({ field: { onChange } }) => (
                      <LegacyRichTextarea onChange={onChange} />
                    )}
                  />
                </Grid>
              </Grid>
            </form>
          </>
        )}
      </LegacyDialog>
      {errorMessage && errorDialogVisible ? (
        <LegacyAlertDialog
          open={open}
          title={'laError'}
          onClose={onErrorDialogClose}
        >
          <Typography variant="body2">{errorMessage}</Typography>
        </LegacyAlertDialog>
      ) : undefined}
    </>
  );
};
